import { ChevronDown, ChevronRight } from "lucide-react";
import { type ReactNode, useState } from "react";

/** What a step found, shown in a card under it (Figma "Detail"). */
export interface ResponseProgressStepDetail {
  /** "Found 3 documents in Atlas Health knowledge". */
  title?: ReactNode;
  /** "query: …", "results: …". */
  lines?: ReactNode[];
  /** Indented under the lines ("Sales Playbook, 6 passages"). */
  items?: ReactNode[];
}

export interface ResponseProgressStep {
  id: string;
  /** What the step did ("Searched Atlas Health knowledge"). */
  label: ReactNode;
  /** A step that failed reads in the error tone. */
  status?: "done" | "error";
  /** Opens under the step when it's clicked. Without it the step is plain text. */
  detail?: ResponseProgressStepDetail;
}

export interface ResponseProgressProps {
  /** What the reply is doing now ("Searching Atlas Health knowledge"). Announced politely as it changes. */
  label: ReactNode;
  /** What it's doing it with, after the label in a quieter tone (": Atlas Health RFP pricing"). */
  labelDetail?: ReactNode;
  /** How long the reply has been working, already formatted ("2m 10s"). Kept out of the announcement. */
  elapsed?: ReactNode;
  /** The steps it has taken so far. When there are any, the status line opens and closes the list. */
  steps?: ResponseProgressStep[];
  defaultOpen?: boolean;
}

const LABEL = "[font:var(--type-body-xs)]";
const TOGGLE = "inline-flex cursor-pointer items-center rounded text-left btn-focus-ring";

/** The AI's loading dots (Figma "Loading dots"): four brand dots in a 1.32s swing. Still under reduced motion. */
export function ResponseDots() {
  return (
    <span aria-hidden="true" className="loading-dots">
      <i />
      <i />
      <i />
      <i />
    </span>
  );
}

function Detail({ detail }: Readonly<{ detail: ResponseProgressStepDetail }>) {
  return (
    <div className="flex flex-col gap-1.5 rounded-lg border border-border-default bg-surface-primary px-3 py-2.5">
      {detail.title && <p className="[font:var(--type-caption)] text-content-primary">{detail.title}</p>}
      {Boolean(detail.lines?.length || detail.items?.length) && (
        <div className="[font:var(--type-body-2xs)] text-content-secondary">
          {detail.lines?.map((line, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: a step's lines are fixed once it's done
            <p key={i}>{line}</p>
          ))}
          {detail.items && detail.items.length > 0 && (
            <ul className="pl-2">
              {detail.items.map((item, i) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: two items can read the same
                <li key={i}>{item}</li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

function Step({ step }: Readonly<{ step: ResponseProgressStep }>) {
  const [open, setOpen] = useState(false);
  const tone = step.status === "error" ? "text-error-on-subtle" : "text-content-secondary";
  if (!step.detail) {
    return <li className={`${LABEL} ${tone}`}>{step.label}</li>;
  }
  const Chevron = open ? ChevronDown : ChevronRight;
  return (
    <li className="flex flex-col gap-1.5">
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className={`${TOGGLE} gap-2 self-start ${LABEL} ${tone} transition-colors hover:text-content-primary`}
      >
        <span>{step.label}</span>
        <Chevron size={14} className="shrink-0 text-content-tertiary" />
      </button>
      {open && <Detail detail={step.detail} />}
    </li>
  );
}

/**
 * The AI's reply while it works (Figma "Generating message"): dots, what it's doing now and how long it has taken,
 * with the steps so far opened from that line, each opening to what it found. A person typing uses its own indicator
 * (grey dots, above the composer).
 */
export function ResponseProgress({
  label,
  labelDetail,
  elapsed,
  steps = [],
  defaultOpen = false,
}: Readonly<ResponseProgressProps>) {
  const [open, setOpen] = useState(defaultOpen);
  const hasSteps = steps.length > 0;
  const Chevron = open ? ChevronDown : ChevronRight;
  const line = (
    <>
      <span className={`min-w-0 truncate ${LABEL} text-content-secondary`}>
        {label}
        {labelDetail != null && <span className="text-content-tertiary">{labelDetail}</span>}
      </span>
      {elapsed != null && <span className="shrink-0 text-xs tabular-nums text-content-tertiary">{elapsed}</span>}
      {hasSteps && <Chevron size={14} className="shrink-0 text-content-tertiary" />}
    </>
  );
  return (
    <div className="relative flex flex-col gap-2.5">
      {/* Announced on its own: a live region inside the button would drop out of the button's name. */}
      <span role="status" className="sr-only">
        {label}
        {labelDetail}
      </span>
      <div className="flex min-w-0 items-center gap-3">
        <ResponseDots />
        {hasSteps ? (
          <button
            type="button"
            aria-expanded={open}
            onClick={() => setOpen((o) => !o)}
            className={`${TOGGLE} min-w-0 gap-3 transition-colors hover:text-content-primary`}
          >
            {line}
          </button>
        ) : (
          <div className="flex min-w-0 items-center gap-3">{line}</div>
        )}
      </div>
      {open && hasSteps && (
        <ul className="flex flex-col gap-1.5 pl-[41px]">
          {steps.map((step) => (
            <Step key={step.id} step={step} />
          ))}
        </ul>
      )}
    </div>
  );
}
